import { useSettings } from "../../context/SettingsContext";
import { Toggle, ButtonGroup, SettingRow, SectionCard } from "../ui/index";

export default function VideoPanel() {
    const {
        state: { settings },
        actions,
    } = useSettings();
    const s = (key, val) => actions.set(key, val);

    return (
        <div className="space-y-5">
            <SectionCard
                title="How to Add Videos"
                description="Videos are added per-product. Go to the product edit page in WooCommerce and use the 'Product Gallery Videos' metabox in the sidebar to add YouTube, Vimeo, or uploaded video files."
            >
                <div className="mt-2 mb-2 flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2.5 text-xs text-blue-800">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="mt-0.5 size-3.5 shrink-0 text-blue-500"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="8" x2="12" y2="12" />
                        <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    <span>
                        Videos appear after product images in the gallery
                        thumbnail strip. Clicking a video thumbnail plays it in
                        the main display area. These settings control playback
                        behaviour across all products using this rule.
                    </span>
                </div>
            </SectionCard>

            <SectionCard
                title="General"
                description="Global video behaviour settings."
            >
                <SettingRow
                    label="Video Position"
                    description="Where product videos appear in the gallery thumbnail strip — before or after product images."
                >
                    <ButtonGroup
                        value={settings.videoPosition}
                        onChange={(v) => s("videoPosition", v)}
                        options={[
                            { label: "Start", value: "start" },
                            { label: "End",   value: "end"   },
                        ]}
                    />
                </SettingRow>
            </SectionCard>

            <SectionCard
                title="Embed Playback"
                description="Settings for YouTube and Vimeo embeds."
            >
                <SettingRow
                    label="Autoplay"
                    description="Automatically start playing the embed when navigated to. Most browsers require the video to also be muted for autoplay to work."
                >
                    <Toggle
                        value={settings.videoAutoplay}
                        onChange={(v) => s("videoAutoplay", v)}
                    />
                </SettingRow>

                {settings.videoAutoplay && (
                    <SettingRow
                        label="Muted"
                        description="Mute the embed by default. Required for autoplay in most browsers. Users can unmute inside the player."
                    >
                        <Toggle
                            value={settings.videoMuted}
                            onChange={(v) => s("videoMuted", v)}
                        />
                    </SettingRow>
                )}

                <SettingRow
                    label="Loop"
                    description="Restart the video from the beginning when it ends."
                >
                    <Toggle
                        value={settings.videoLoop}
                        onChange={(v) => s("videoLoop", v)}
                    />
                </SettingRow>

                <SettingRow
                    label="Hide Related Videos"
                    description="For YouTube: hide suggested videos shown after the video ends (sets rel=0)."
                >
                    <Toggle
                        value={settings.videoHideRelated}
                        onChange={(v) => s("videoHideRelated", v)}
                    />
                </SettingRow>

                <SettingRow
                    label="Show Controls"
                    description="Display the player controls bar inside the YouTube / Vimeo embed (play, pause, volume, fullscreen)."
                >
                    <Toggle
                        value={settings.videoEmbedControls}
                        onChange={(v) => s("videoEmbedControls", v)}
                    />
                </SettingRow>

                <SettingRow
                    label="Fit to Container"
                    description="Stretch the embed to fill the full width and height of the main image area. The video will be cropped as needed to remove any empty space."
                >
                    <Toggle
                        value={settings.videoEmbedFitContainer}
                        onChange={(v) => s("videoEmbedFitContainer", v)}
                    />
                </SettingRow>
            </SectionCard>

            <SectionCard
                title="Hosted Video"
                description="Settings for video files uploaded to the WordPress media library."
            >
                <SettingRow
                    label="Autoplay"
                    description="Automatically start playing the hosted video when navigated to. For silent autoplay, enable Muted as well."
                >
                    <Toggle
                        value={settings.videoHostedAutoplay}
                        onChange={(v) => s("videoHostedAutoplay", v)}
                    />
                </SettingRow>

                <SettingRow
                    label="Muted"
                    description="Mute hosted videos by default. Required for autoplay in most browsers. Useful for background-style videos."
                >
                    <Toggle
                        value={settings.videoHostedMuted}
                        onChange={(v) => s("videoHostedMuted", v)}
                    />
                </SettingRow>

                <SettingRow
                    label="Show Controls"
                    description="Display the browser's native video controls (play, pause, seek, volume)."
                >
                    <Toggle
                        value={settings.videoControls}
                        onChange={(v) => s("videoControls", v)}
                    />
                </SettingRow>

                <SettingRow
                    label="Loop"
                    description="Restart hosted videos from the beginning when they end."
                >
                    <Toggle
                        value={settings.videoHostedLoop}
                        onChange={(v) => s("videoHostedLoop", v)}
                    />
                </SettingRow>

                <SettingRow
                    label="Fit to Container"
                    description="Stretch the video to fill the full width and height of the main image area. The video will be cropped as needed to remove any empty space."
                >
                    <Toggle
                        value={settings.videoHostedFitContainer}
                        onChange={(v) => s("videoHostedFitContainer", v)}
                    />
                </SettingRow>
            </SectionCard>
        </div>
    );
}
